import type { ChatProvider } from '@xsai-ext/shared-providers'

import { computed } from 'vue'

import { useCallStore } from '../stores/call'
import { useChatStore } from '../stores/chat'

interface CallSendOptions {
  providerId: string
  model: string
  chatProvider: ChatProvider
  providerConfig?: Record<string, unknown>
}

/**
 * 增强型AI通话功能
 */
export function useEnhancedCall() {
  const chatStore = useChatStore()
  const callStore = useCallStore()

  /**
   * AI主动发起通话
   * 这个功能需要在聊天消息处理中检测AI的意图
   */
  const handleAIInitiateCall = (callType: 'voice' | 'video') => {
    // 设置来电状态，触发来电UI
    callStore.setIncomingCall(
      callType,
      'current-character-id', // 需要从当前聊天上下文获取
      'AI角色名称', // 需要从当前角色信息获取
    )
  }

  /**
   * 检测AI是否想要发起通话
   * 在AI回复中查找通话意图
   */
  const detectCallIntent = (aiMessage: string): { type: 'voice' | 'video' | null, greeting?: string } => {
    // 提取文本内容
    let textContent = aiMessage
    let extractedGreeting: string | undefined

    try {
      // 优先尝试解析包含 type 字段的 JSON（AI主动发起通话的格式）
      const typeJsonMatch = aiMessage.match(/\{[\s\S]*?"type"\s*:\s*"initiate_(voice|video)_call"[\s\S]*?\}/)
      if (typeJsonMatch) {
        const parsed = JSON.parse(typeJsonMatch[0])
        if (parsed.type === 'initiate_voice_call' || parsed.type === 'initiate_video_call') {
          extractedGreeting = parsed.greeting || undefined
          const callType = parsed.type === 'initiate_video_call' ? 'video' : 'voice'
          return { type: callType, greeting: extractedGreeting }
        }
      }

      // 尝试提取JSON中的text字段
      const jsonMatch = aiMessage.match(/\{[\s\S]*?"text"[\s\S]*?\}/)
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0])
        textContent = parsed.text || aiMessage
      }
    }
    catch {
      // JSON解析失败，使用原始文本
    }

    const lowerMessage = textContent.toLowerCase()

    // 🔧 排除否定/拒绝/疑问的情况
    const negativePatterns = [
      '不.*电话',
      '不.*通话',
      '不.*视频',
      '不想.*电话',
      '不想.*通话',
      '要不要.*电话', // "要不要打电话？"是疑问
      '要.*电话.*吗', // "要打电话吗？"是疑问
      '能.*电话.*吗', // "能打电话吗？"是疑问
      '可以.*电话.*吗', // "可以打电话吗？"是疑问
      '等.*再.*电话',
      '等.*再.*通话',
      '先.*文字',
      '现在.*不',
      '暂时.*不',
    ]

    // 检查是否包含"稍等"、"马上"等积极词汇，如果有则跳过否定检测
    const hasPositiveIntent = lowerMessage.includes('稍等') || lowerMessage.includes('马上') || lowerMessage.includes('立刻') || lowerMessage.includes('现在就')

    if (!hasPositiveIntent) {
      for (const pattern of negativePatterns) {
        const regex = new RegExp(pattern)
        if (regex.test(lowerMessage)) {
          return { type: null } // 检测到否定/拒绝意图，不发起通话
        }
      }
    }

    // 检测视频通话意图（优先级高）
    const videoCallKeywords = [
      '发起视频通话',
      'initiate_video_call',
      '我想和你视频',
      '我们视频通话',
      '视频聊聊',
    ]
    if (videoCallKeywords.some(keyword => lowerMessage.includes(keyword))) {
      return { type: 'video' }
    }

    // 检测语音通话意图
    const voiceCallKeywords = [
      '发起语音通话',
      'initiate_voice_call',
      '我想给你打个电话',
      '我给你打电话',
      '我现在给你打电话',
      '我们语音通话',
      '语音聊聊',
      '马上拨过去',
      '现在拨过去',
      '马上打过去',
      '立刻拨过去',
      '这不就在打',
      '就在打',
      '正在打',
      '给你打过去',
      '打过去',
      '这就打',
      '现在就打',
    ]
    const voiceCallPatterns = [
      /稍等.{0,10}拨/,
      /稍等.{0,10}打/,
      /这.{0,5}就.{0,5}打/,
      /正在.{0,10}打/,
    ]
    if (voiceCallKeywords.some(keyword => lowerMessage.includes(keyword))
      || voiceCallPatterns.some(pattern => pattern.test(lowerMessage))) {
      return { type: 'voice' }
    }

    return { type: null }
  }

  /**
   * 用户发起呼出通话（增强版）
   * 支持AI智能决策接听/拒绝
   */
  const initiateCallWithAIDecision = async (
    type: 'voice' | 'video',
    characterId: string,
    characterName: string,
    characterAvatar?: string,
    sendOptions?: CallSendOptions,
  ) => {
    if (callStore.isCallActive || callStore.outgoingCallPending) {
      console.warn('Call already in progress')
      return false
    }

    // 设置呼出等待状态
    callStore.setOutgoingCallPending(type, characterId, characterName, characterAvatar)

    const callTypeText = type === 'voice' ? '语音' : '视频'
    const systemMessage = {
      role: 'system',
      content: `
[系统提示：用户正在向你发起${callTypeText}通话请求。你必须立即做出决定。]

**决策原则**：
1. 如果你与用户的关系正常（没有明显冲突或负面情绪），默认接受通话
2. 只有在以下情况才拒绝：
   - 你的人设中明确不喜欢接电话
   - 当前情境明显不适合（如深夜、你正在忙碌等）
   - 与用户关系很差或正在冷战
3. 如果不确定，倾向于接受（保持友好）

**你必须立即调用 answer_call 工具做出回应**：
- decision: "accept" 接听 / "reject" 拒绝
- message: 如果接受，填写接通后的第一句问候语；如果拒绝，填写拒绝理由

示例：
- 接受：answer_call(decision="accept", message="喂？怎么啦~")
- 拒绝：answer_call(decision="reject", message="不好意思，我现在有点忙，待会儿再说吧")

**注意**：不要犹豫，立即调用工具做出决定。
      `.trim(),
      metadata: { type: 'call_system_prompt' },
    }

    try {
      // 添加系统消息（使用赋值触发响应式）
      chatStore.messages = [...chatStore.messages, systemMessage as any]

      // 如果提供了sendOptions，触发AI回复
      if (sendOptions) {
        await chatStore.send('', {
          ...sendOptions,
          skipUserMessage: true, // 跳过添加用户消息
        })
      }

      return true
    }
    catch (error) {
      console.error('Failed to initiate call with AI decision:', error)
      callStore.cancelOutgoingCall()
      return false
    }
  }

  /**
   * 处理AI的通话决策回复
   * 需要在聊天store的消息处理流程中调用
   */
  const handleAICallDecision = (aiResponse: string) => {
    // 尝试解析JSON响应
    try {
      const jsonMatch = aiResponse.match(/\{[\s\S]*?"decision"[\s\S]*?\}/)
      if (!jsonMatch)
        return null

      const decision = JSON.parse(jsonMatch[0])

      if (decision.decision === 'accept') {
        // AI接受了通话，同时提取问候语
        return {
          accepted: true,
          reason: decision.reason,
          greeting: decision.greeting || '',
        }
      }
      else if (decision.decision === 'reject') {
        // AI拒绝了通话
        return {
          accepted: false,
          reason: decision.reason,
          greeting: '',
        }
      }
    }
    catch (error) {
      console.error('Failed to parse AI call decision:', error)
    }

    return null
  }

  /**
   * AI在通话中主动挂断
   */
  const handleAIHangUp = (reason?: string) => {
    if (!callStore.isCallActive)
      return

    // 添加AI的最后一句话到转录
    if (reason) {
      callStore.addToTranscript({
        role: 'assistant',
        content: reason,
        timestamp: Date.now(),
      })
    }

    // 延迟1.5秒后挂断，让用户有时间看到AI说的话
    setTimeout(() => {
      const callLog = callStore.endCall()

      // 在聊天历史中添加通话结束消息（仅显示时长，不包含聊天内容）
      if (callLog) {
        const endMessage = `通话结束，时长 ${callStore.formatDuration(callLog.duration)}`
        chatStore.messages = [...chatStore.messages, {
          role: 'system',
          content: endMessage,
          metadata: {
            type: 'call_end',
            callType: callLog.type,
            duration: callLog.duration,
            startTime: callLog.startTime,
          },
        } as any]
      }

      // 确保关闭 typing 气泡
      chatStore.setAiTypingState(false)
    }, 1500)
  }

  /**
   * 检测AI是否想要挂断
   */
  const detectHangUpIntent = (aiMessage: string): { hangUp: boolean, reason?: string } => {
    const hangUpKeywords = ['hang_up_call', '挂断电话', '我要挂了']

    const lowerMessage = aiMessage.toLowerCase()

    for (const keyword of hangUpKeywords) {
      if (lowerMessage.includes(keyword)) {
        // 尝试提取理由
        const match = aiMessage.match(/reason['":\s]+([^"'}]+)/)
        return {
          hangUp: true,
          reason: match ? match[1] : undefined,
        }
      }
    }

    return { hangUp: false }
  }

  // 导出所有功能
  return {
    // AI主动功能
    handleAIInitiateCall,
    detectCallIntent,

    // 智能决策
    initiateCallWithAIDecision,
    handleAICallDecision,

    // AI挂断
    handleAIHangUp,
    detectHangUpIntent,

    // 基础状态
    isCallActive: computed(() => callStore.isCallActive),
    outgoingCallPending: computed(() => callStore.outgoingCallPending),
    incomingCallOffer: computed(() => callStore.incomingCallOffer),
  }
}
